import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { GlobalToastTypeNames, ToastTypes } from 'types';
import {
  useStudentLmsBlockQuery,
  useIssueLmsBlockMutation,
  useLiftLmsBlockMutation,
  StudentLmsBlockDocument,
} from 'core/graphql';
import {
  buildLmsBlockVariables,
  ILmsAmountDue,
  ILmsBlockInvoice,
} from 'core/utils/lmsSuspension';

import { useToasts } from '../Toast/useToasts';
import { lmsBlockErrorText } from './lmsBlockErrors';
import { useStudentPermissions } from './useStudentPermissions';

export interface ILmsBlockActionOptions {
  /** Free-text reason recorded on the event (block only in the UI). */
  reason?: string;
  /** Email + Slack DM to the student. Defaults to true (ТЗ 0074 §3.4). */
  notifyStudent?: boolean;
}

export interface IUseLmsAccessBlock {
  isBlocked: boolean;
  reason: string | null;
  /** `robot` | `manual` — who suspended (null when never / not blocked). */
  source: string | null;
  blockedAt: string | null;
  blockedByName: string | null;
  /** Invoices the active suspension is tied to (empty when not blocked). */
  blockingInvoices: ILmsBlockInvoice[];
  /** Still owed on those invoices, one entry per currency. */
  amountsDue: ILmsAmountDue[];
  loading: boolean;
  /** Suspend access; tied server-side to ALL current unpaid invoices. */
  block: (options?: ILmsBlockActionOptions) => Promise<boolean>;
  /** Restore access; the suspension's overdue invoices are forgiven. */
  unblock: (options?: ILmsBlockActionOptions) => Promise<boolean>;
}

const EMPTY_INVOICES: ILmsBlockInvoice[] = [];
const EMPTY_AMOUNTS: ILmsAmountDue[] = [];

/**
 * Read + toggle a student's LMS-access suspension (spec 0003 → ТЗ 0074).
 *
 * The read is skipped for roles outside PAYMENT_ACCESS_ROLES: the backend
 * refuses them, and a refused query on the student card is an error, not an
 * empty state. Toasts on failure; refetches the block so the header flips.
 */
export const useLmsAccessBlock = (studentId?: string): IUseLmsAccessBlock => {
  const { t } = useTranslation();
  const { addToast } = useToasts();
  const { canBlockLmsAccess } = useStudentPermissions();

  const { data, loading: queryLoading } = useStudentLmsBlockQuery({
    variables: { studentId: studentId || '' },
    skip: !studentId || !canBlockLmsAccess,
    fetchPolicy: 'cache-and-network',
  });

  const opts = useMemo(
    () => ({
      refetchQueries: studentId
        ? [{ query: StudentLmsBlockDocument, variables: { studentId } }]
        : [],
      awaitRefetchQueries: true,
    }),
    [studentId],
  );

  const [blockMut, blockState] = useIssueLmsBlockMutation(opts);
  const [unblockMut, unblockState] = useLiftLmsBlockMutation(opts);

  const notifyError = useCallback(
    (text?: string) => {
      addToast({
        data: {
          type: ToastTypes.error,
          title: t('pages.studentDetail.lmsBlock.failed'),
          text,
        },
        type: GlobalToastTypeNames.LmsAccessBlock,
      });
    },
    [addToast, t],
  );

  const notifySuccess = useCallback(
    (title: string) => {
      addToast({
        data: {
          type: ToastTypes.success,
          title,
        },
        type: GlobalToastTypeNames.LmsAccessBlock,
      });
    },
    [addToast],
  );

  const block = useCallback(
    async (options?: ILmsBlockActionOptions) => {
      if (!studentId) return false;
      try {
        const res = await blockMut({
          variables: buildLmsBlockVariables(studentId, options),
        });
        const payload = res.data?.blockStudentLmsAccess;
        if (payload?.success) {
          notifySuccess(t('pages.studentDetail.lmsBlock.blocked'));
          return true;
        }
        notifyError(lmsBlockErrorText(payload?.customErrors, t));
        return false;
      } catch (e) {
        notifyError((e as Error).message);
        return false;
      }
    },
    [blockMut, studentId, notifyError, notifySuccess, t],
  );

  const unblock = useCallback(
    async (options?: ILmsBlockActionOptions) => {
      if (!studentId) return false;
      try {
        const res = await unblockMut({
          variables: buildLmsBlockVariables(studentId, options),
        });
        const payload = res.data?.unblockStudentLmsAccess;
        if (payload?.success) {
          notifySuccess(t('pages.studentDetail.lmsBlock.unblocked'));
          return true;
        }
        notifyError(lmsBlockErrorText(payload?.customErrors, t));
        return false;
      } catch (e) {
        notifyError((e as Error).message);
        return false;
      }
    },
    [unblockMut, studentId, notifyError, notifySuccess, t],
  );

  const record = data?.studentAccessBlock;
  const isBlocked = Boolean(record?.isBlocked);

  return {
    isBlocked,
    reason: record?.reason ?? null,
    source: isBlocked ? record?.source ?? null : null,
    blockedAt: isBlocked ? record?.blockedAt ?? null : null,
    blockedByName: isBlocked ? record?.blockedByName ?? null : null,
    blockingInvoices: record?.blockingInvoices ?? EMPTY_INVOICES,
    amountsDue: record?.amountsDue ?? EMPTY_AMOUNTS,
    loading: queryLoading || blockState.loading || unblockState.loading,
    block,
    unblock,
  };
};
